import { SelectHTMLAttributes, forwardRef } from 'react';
import { SelectMultiple, type SelectMultipleProps } from './SelectMultiple';
import './Select.css';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectSingleProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Native single-value select (default). */
  variant?: 'single';
  label?: string;
  error?: string;
  hint?: string;
  options?: SelectOption[];
  placeholder?: string;
  fullWidth?: boolean;
  size?: SelectSize;
}

export type { SelectMultipleProps };

/**
 * Select atom. `variant="single"` (default) is a native <select>;
 * `variant="multiple"` is a capped multi-select (checkbox list behind a
 * disclosure button, `maxSelected` + visible `hint`) — #1653.
 */
export type SelectProps = SelectSingleProps | SelectMultipleProps;

export const Select = forwardRef<HTMLSelectElement, SelectProps>((props, ref) =>
  props.variant === 'multiple' ? <SelectMultiple {...props} /> : <SelectSingle {...props} ref={ref} />,
);

const SelectSingle = forwardRef<HTMLSelectElement, SelectSingleProps>(
  (
    {
      variant: _variant,
      label,
      error,
      hint,
      options,
      placeholder,
      fullWidth = false,
      size = 'md',
      id,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const selectId = id ?? (label ? `select-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);
    const hasError = error != null && error !== '';

    const wrapperClass = [
      'select-wrapper',
      fullWidth ? 'select-wrapper--full-width' : '',
      rest.disabled ? 'select-wrapper--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const selectClass = [
      'select__field',
      `select__field--${size}`,
      hasError ? 'select__field--error' : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClass}>
        {label != null && (
          <label className="select-label" htmlFor={selectId}>
            {label}
            {rest.required && (
              <span className="select-label__required" aria-hidden="true">
                {' '}*
              </span>
            )}
          </label>
        )}
        <div className="select-control-wrapper">
          <select
            ref={ref}
            id={selectId}
            className={selectClass}
            aria-invalid={hasError ? 'true' : undefined}
            aria-describedby={
              hasError
                ? `${selectId}-error`
                : hint != null
                ? `${selectId}-hint`
                : undefined
            }
            {...rest}
          >
            {placeholder != null && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options != null
              ? options.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </option>
                ))
              : children}
          </select>
          <span className="select-chevron" aria-hidden="true">
            ▾
          </span>
        </div>
        {hasError && (
          <span id={`${selectId}-error`} className="select-error" role="alert">
            {error}
          </span>
        )}
        {!hasError && hint != null && (
          <span id={`${selectId}-hint`} className="select-hint">
            {hint}
          </span>
        )}
      </div>
    );
  },
);

SelectSingle.displayName = 'SelectSingle';
Select.displayName = 'Select';

export default Select;
